import SearchPartsExtraFilters from '@/components/SearchParts/SearchPartsExtraFilters';
import { AppPageHeader, InfiniteTable } from '@/components/_common';
import { Row, Col, Checkbox, Button, Divider, Image } from 'antd';
import usePartsColumns from '@/components/Operational/Inventory/Parts/usePartsColumns';
import PartSimpliedRegisterModal, {
  OnCallbackAddPart,
} from '@/components/_common/PartSimpliedRegisterModal';
import { useStockItems } from '@/core/domain/stock/StockItems/StockItems.hooks';
import {
  ListStockItemsQuery,
  StockItemType,
  StockOrderColumn,
  FilterTypeEnum,
  StockItem,
} from '@/core/domain/stock/StockItems/StockItems.types';
import { useWorkOrderItemsVerify } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { useWindowSize, useDisclosure, useDebounce } from '@/hooks';
import { useState, useEffect } from 'react';
import { WorkorderItem, getCompanyPrice, useWorkorderItems } from '.';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { WorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { WorkOrderComplement } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import partsOrderColumns from '@/utils/functions/partsOrderColumns/partsOrderColumns';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';

const PartsSystemList: React.FC = () => {
  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const { data: matrixData } = useMatrix();

  const { columns, isFetching: isFetchingColumns } = usePartsColumns({
    hasSorter: true,
    showImage: true,
  });

  const { items, setItems } = useWorkorderItems();

  const { isMobile } = useWindowSize();

  const [callbackPart, setCallbackPart] = useState<OnCallbackAddPart>();
  const [partCreate, actionPartCreate] = useDisclosure(false, {
    onOpen() {
      setCallbackPart(undefined);
    },
  });

  const { data: workorderItemsData } = useWorkOrderItemsVerify(complement?.id);
  const workorderItems = workorderItemsData?.data || [];

  const [queryParams, setQueryParams] = useState<ListStockItemsQuery>({
    companiesIds: workorder ? [workorder?.company.id] : [],
    order: 'ASC',
    itemType: StockItemType.Part,
    orderColumns: StockOrderColumn.partId,
    filterType: FilterTypeEnum.free,
    active: true,
    vehicleId: undefined,
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, hasNextPage, fetchNextPage } = useStockItems(
    !!workorder?.company.id,
    {
      ...queryDebounce,
      vehicleId: matrixData?.matrixSettings.defaultPartApplication
        ? queryDebounce?.vehicleId
        : undefined,
    },
    callbackPart?.onRefresh,
  );

  const stockItems = flatInfiniteResult(data);

  useEffect(() => {
    if (callbackPart) {
      setQueryParams((prev) => ({
        ...prev,
        search: callbackPart.part.name,
      }));
    }
  }, [callbackPart]);

  return (
    <>
      <AppPageHeader
        searchValue={queryParams.search}
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        extraFilters={
          <SearchPartsExtraFilters setQueryParams={setQueryParams} />
        }
      />

      <Row align="middle" gutter={[12, 12]}>
        <>
          <Col>
            {!!workorder?.vehicle && (
              <Checkbox
                checked={!!queryParams.vehicleId}
                onChange={({ target }) =>
                  setQueryParams((prev) => ({
                    ...prev,
                    vehicleId: target.checked
                      ? workorder?.vehicle.id
                      : undefined,
                    partApplicationDescription:
                      !matrixData?.matrixSettings.defaultPartApplication &&
                      target.checked
                        ? workorder.vehicle.model
                        : undefined,
                  }))
                }
              >
                {`Mostrar somente peças do modelo ${workorder?.vehicle.model}`}
              </Checkbox>
            )}
          </Col>

          <Col flex={1}>
            <Checkbox
              checked={!!queryParams.isPartReplacement}
              onChange={({ target }) =>
                setQueryParams((prev) => ({
                  ...prev,
                  isPartReplacement: target.checked,
                }))
              }
            >
              Mostrar somente peças substitutas
            </Checkbox>
          </Col>

          <Col flex={1}>
            <Checkbox
              checked={queryParams?.availableStock}
              onChange={({ target: { checked } }) =>
                setQueryParams((prev) => ({
                  ...prev,
                  availableStock: checked,
                }))
              }
              style={{ marginTop: 10 }}
            >
              Mostrar peça com estoque disponível
            </Checkbox>
          </Col>
        </>

        <Col span={isMobile ? 24 : undefined}>
          <Button type="primary" block onClick={actionPartCreate.open}>
            Cadastrar peça
          </Button>
        </Col>
      </Row>

      <Divider />

      <Image.PreviewGroup>
        <InfiniteTable<StockItem>
          columns={columns}
          onChange={(_p, _f, sorter) => {
            if (!Array.isArray(sorter)) {
              partsOrderColumns(sorter, setQueryParams);
            }
          }}
          dataSource={stockItems}
          rowKey={(row) => row.partId}
          loading={isFetching || isFetchingColumns}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          rowSelection={{
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            onChange: (_, parts) => {
              if (complement || workorder) {
                setItems([
                  ...parts.map<WorkorderItem>((part) => ({
                    complementId: (complement as WorkOrderComplement).id,
                    itemId: part?.partId,
                    itemName: part?.description,
                    itemType: WorkOrderItemType.Part,
                    subAreaId: part?.subArea?.id,
                    unitPrice: getCompanyPrice(
                      part?.prices.map((price) => ({
                        price: price.price,
                        companyId: price.company.id,
                      })),
                      (workorder as WorkOrder).company.id,
                    ),
                    unitCost: getCompanyPrice(
                      part?.prices.map((price) => ({
                        price: price.price,
                        companyId: price.company.id,
                        realCost: price.realCost,
                      })),
                      (workorder as WorkOrder).company.id,
                      'realCost',
                    ),
                    manufacturerId: part?.manufacturer?.id,
                    quantity: 1,
                    manufacturingCode: part?.manufacturingCode,
                    discount: 0,
                    isInternal: true,
                    observation: part?.observation,
                  })),
                ]);
              }
            },
            selectedRowKeys: items.map((part) => part?.itemId),
            getCheckboxProps: (item) => ({
              disabled: !!workorderItems.find(
                (workorderItem) =>
                  workorderItem.type === WorkOrderItemType.Part &&
                  item.partId === workorderItem.itemId,
              ),
            }),
          }}
        />
      </Image.PreviewGroup>

      <PartSimpliedRegisterModal
        open={partCreate}
        onCancel={actionPartCreate.close}
        onCallback={setCallbackPart}
      />
    </>
  );
};

export default PartsSystemList;
